import { Container, Item } from 'Components/Grid';
import { isNumber } from 'neuro-utils';
import { formatPartialDate } from 'Utility/date';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import EventStepper from 'Components/EventStepper';
import { StepperHeaderFormInputPair, StepperHeaderValuePair } from 'Components/EventStepper/components';
import FormSection from 'Components/FormSection';
import InputHandler from 'Components/InputHandler';
import Unit from 'Components/Unit';
import type { IFormContext } from 'Containers/FormContextHandler';
import { withFormContext } from 'Containers/FormContextHandler';
import type { IOxygenTherapyTreatmentResponses } from 'neuro-schemas/oxygenTherapy';

const ResponseEventContent = ({ response }: { response: IOxygenTherapyTreatmentResponses }): React.JSX.Element => (
  <React.Fragment>
    <StepperHeaderValuePair
      header={<FormattedMessage id={'treatment.oxygenTherapy.treatmentResponses.oxygenSaturation'} />}
      value={
        isNumber(response.oxygenSaturation) ? (
          <>
            {`${response.oxygenSaturation} `}
            <FormattedMessage id={'treatment.oxygenTherapy.treatmentResponses.percent'} />
          </>
        ) : null
      }
    />
  </React.Fragment>
);

const ResponseFormContent = ({
  thisResponse,
  onChange,
  mainDate,
}: {
  thisResponse?: IOxygenTherapyTreatmentResponses;
  onChange: IFormData['onChange'];
  mainDate?: PartialDate;
}): React.JSX.Element => {
  const locPath = 'treatment.oxygenTherapy.treatmentResponses';

  return (
    <>
      <StepperHeaderFormInputPair
        header={<FormattedMessage id={`general.date`} />}
        input={
          <InputHandler
            type="PartialDate"
            editing={true}
            name="date"
            formData={{
              onChange,
              document: {
                date: thisResponse?.date,
              },
            }}
            dateDefault={mainDate ?? 'now'}
            dateHook={{ dateHookFloor: mainDate }}
            isNotCancellable={true}
          />
        }
      />
      <StepperHeaderFormInputPair
        header={<FormattedMessage id={`${locPath}.oxygenSaturation`} />}
        input={
          <Unit unit={<FormattedMessage id={`${locPath}.percent`} />}>
            <InputHandler
              type="NumberField"
              editing={true}
              name="oxygenSaturation"
              formData={{
                onChange,
                document: {
                  oxygenSaturation: thisResponse?.oxygenSaturation,
                },
              }}
              precision={2}
              placeholder={`${locPath}.oxygenSaturation`}
            />
          </Unit>
        }
      />
    </>
  );
};

const locPath = 'treatment.oxygenTherapy.';

const OxygenTherapyTreatmentResponses = ({
  formData,
  view,
}: IFormContext<IDocumentWithTypeV2<'oxygenTherapy'>, any>): React.JSX.Element => {
  return (
    <FormSection header={locPath + 'treatmentResponses.' + 'title'}>
      <EventStepper
        name="treatmentResponses"
        formData={formData}
        stepLabelText={(d: IOxygenTherapyTreatmentResponses): string => formatPartialDate(d.date)}
        stepContent={(d: IOxygenTherapyTreatmentResponses): React.JSX.Element => <ResponseEventContent response={d} />}
        addNewTextHeader={locPath + 'treatmentResponses.newTreatmentResponse'}
        addNewTextButton={locPath + 'treatmentResponses.newTreatmentResponse'}
        previousEventsTextHeader={locPath + 'treatmentResponses.previousTreatmentResponses'}
        editingElements={(index: number, onChange: IFormData['onChange']): React.JSX.Element => {
          const thisResponse = formData.document.treatmentResponses?.[index];
          return (
            <ResponseFormContent
              thisResponse={thisResponse}
              onChange={onChange}
              mainDate={
                formData.document.treatmentResponses && formData.document.treatmentResponses.length < 2
                  ? (formData.document.date ?? undefined)
                  : undefined
              }
            />
          );
        }}
        viewing={!!view?.viewing}
        tableContent={(events: IOxygenTherapyTreatmentResponses[]) => (
          <Container alignItems="baseline" style={{ marginBottom: '4.5rem' }}>
            <Item xs={3} style={{ paddingLeft: '2rem' }}>
              <FormattedMessage id={locPath + 'treatmentResponses.oxygenSaturation'} />
            </Item>
            {events.slice().map((e: IOxygenTherapyTreatmentResponses, i: number, arr) => {
              return (
                <Item key={i} xs={i === arr.length - 1 ? 1 : 2} style={{ fontWeight: 600 }}>
                  {isNumber(e.oxygenSaturation) ? e.oxygenSaturation : '-'}{' '}
                  <FormattedMessage id={locPath + 'treatmentResponses.percent'} />
                </Item>
              );
            })}
          </Container>
        )}
        tableContentFilter={{
          eventFilter: (events: IOxygenTherapyTreatmentResponses[]) => events.slice().reverse(),
        }}
      />
    </FormSection>
  );
};

export default withFormContext(OxygenTherapyTreatmentResponses);
